import type {
  AddItemResponse,
  AlexaAccountsResponse,
  AlexaLinkCodeResponse,
  ApiResponse,
  CreateListResponse,
  DeleteItemResponse,
  ReorderResponse,
  RotateShareResponse,
  StoredList,
  SyncCreateResponse,
  SyncLinkResponse,
  UndoDeleteResponse,
} from "../types/index.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const REQUEST_TIMEOUT_MS = 15_000;

async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  const json: ApiResponse<T> = await res.json();
  if (!json.ok) {
    throw new ApiError(
      json.error?.code ?? "UNKNOWN",
      json.error?.message ?? "unknown error",
      res.status,
    );
  }
  return json.data as T;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly statusCode: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function createList(name: string, createdByName: string): Promise<CreateListResponse> {
  return post("/api/lists", { name, createdByName, clientRequestId: crypto.randomUUID() });
}

export async function addItem(
  shareToken: string,
  text: string,
  createdByName: string,
): Promise<AddItemResponse> {
  return post("/api/items/add", {
    shareToken,
    text,
    createdByName,
    clientRequestId: crypto.randomUUID(),
  });
}

export async function deleteItem(shareToken: string, itemId: string): Promise<DeleteItemResponse> {
  return post("/api/items/delete", { shareToken, itemId });
}

export async function undoDeleteItem(
  shareToken: string,
  itemId: string,
): Promise<UndoDeleteResponse> {
  return post("/api/items/undo", { shareToken, itemId });
}

export async function reorderItem(
  shareToken: string,
  movedItemId: string,
  prevItemId: string | null,
  nextItemId: string | null,
): Promise<ReorderResponse> {
  return post("/api/items/reorder", { shareToken, movedItemId, prevItemId, nextItemId });
}

export async function rotateShareUrl(shareToken: string): Promise<RotateShareResponse> {
  return post("/api/share/rotate", { shareToken });
}

export async function backupStorage(displayName: string, lists: StoredList[]): Promise<void> {
  await post("/api/storage/backup", { displayName, lists });
}

export interface RestoreStorageResponse {
  displayName: string;
  lists: StoredList[];
}

export async function restoreStorage(): Promise<RestoreStorageResponse | null> {
  const res = await fetch(`${API_BASE}/api/storage/restore`, {
    credentials: "same-origin",
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  const json = await res.json();
  if (!json.ok) return null;
  return json.data ?? null;
}

export async function syncCreate(
  displayName: string,
  lists: StoredList[],
): Promise<SyncCreateResponse> {
  return post("/api/sync/create", { displayName, lists });
}

export async function syncLink(
  syncCode: string,
  displayName: string,
  lists: StoredList[],
): Promise<SyncLinkResponse> {
  return post("/api/sync/link", { syncCode, displayName, lists });
}

export async function syncPush(
  syncAccountId: string,
  displayName: string,
  lists: StoredList[],
): Promise<void> {
  await post("/api/sync/push", { syncAccountId, displayName, lists });
}

export async function createAlexaLinkCode(
  shareToken: string,
  displayName: string,
): Promise<AlexaLinkCodeResponse> {
  return post("/api/alexa/link-code", { shareToken, displayName });
}

export async function listAlexaAccounts(shareToken: string): Promise<AlexaAccountsResponse> {
  return post("/api/alexa/accounts", { shareToken });
}

export async function unlinkAlexaAccount(shareToken: string, accountId: string): Promise<void> {
  await post("/api/alexa/unlink", { shareToken, accountId });
}
